import React, { memo, useMemo } from 'react';
import { DateTime } from 'luxon';
import { FormProvider } from 'react-hook-form';
import { useSessionStorage } from 'react-use';
import { useMeContext } from '../../me';
import { useSmartDiaryForm } from './hooks';
import { SmartDiaryEntry } from './type';
import { ApplyToTimesheet } from './ApplyToTimesheet';
import { SmartDiaryListContainer } from './SmartDiaryListContainer';
import {
  SmartDiaryListTabValue,
  useSmartDiaryTabs
} from './hooks/useSmartDiaryTabs';
import { SmartDiaryListTabs } from './SmartDiaryListTabs';
import { SmartDiaryEmptyPage } from './SmartDiaryEmptyPage';
import { useSmartDiaryTabsUpdateState } from './hooks/useSmartDiaryTabsUpdateState';

type SmartDiaryContentProps = {
  smartEntriesByDay: Record<string, SmartDiaryEntry[]>;
  lastSyncTime?: DateTime;
};

export const SmartDiaryContent: React.FC<SmartDiaryContentProps> = memo(
  ({ smartEntriesByDay, lastSyncTime }: SmartDiaryContentProps) => {
    const me = useMeContext();
    const formFields = useSmartDiaryForm(smartEntriesByDay);
    const {
      tabValue,
      setTabValue,
      notAppliedEntries,
      notAppliedCount,
      appliedEntries,
      appliedCount
    } = useSmartDiaryTabs({
      smartEntriesByDay,
      defaultTabValue: SmartDiaryListTabValue.NOT_APPLIED
    });
    const [smartDiarySyncCount, setSmartDiarySyncCount] = useSessionStorage(
      `${me?.id}_smartDiarySyncCount`,
      0
    );
    const hasTabUpdate = useSmartDiaryTabsUpdateState({
      notAppliedEntries,
      tabValue,
      smartDiarySyncCount
    });
    const smartEntriesFilteredByTab =
      tabValue === SmartDiaryListTabValue.NOT_APPLIED
        ? notAppliedEntries
        : tabValue === SmartDiaryListTabValue.APPLIED
        ? appliedEntries
        : smartEntriesByDay;
    const smartEntries = useMemo(
      () =>
        Object.values(smartEntriesFilteredByTab).reduce((acc, value) => {
          return [...acc, ...value];
        }, []),
      [smartEntriesFilteredByTab]
    );
    return (
      <>
        <SmartDiaryListTabs
          tabValue={tabValue}
          setTabValue={setTabValue}
          appliedCount={appliedCount}
          notAppliedCount={notAppliedCount}
          hasTabUpdate={hasTabUpdate}
        />
        {!smartEntries.length && <SmartDiaryEmptyPage tabValue={tabValue} />}
        {/*//to fix type instantiation is excessively deep and infinite
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore */}
        <FormProvider {...formFields}>
          {Boolean(smartEntries.length) && (
            <SmartDiaryListContainer
              smartEntriesByDay={smartEntriesFilteredByTab}
              smartEntries={smartEntries}
              testId={tabValue}
            />
          )}
          <ApplyToTimesheet
            smartEntriesByDay={smartEntriesByDay}
            lastSyncTime={lastSyncTime}
            filteredSmartEntries={smartEntries}
            smartDiarySyncCount={smartDiarySyncCount}
            setSmartDiarySyncCount={setSmartDiarySyncCount}
          />
        </FormProvider>
      </>
    );
  }
);

SmartDiaryContent.displayName = 'SmartDiaryContent';
